import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2,
  ChevronRight,
  UserCircle,
  Briefcase,
  Award,
  FolderGit2,
  GraduationCap,
  FileText,
  Trophy,
  Building2,
  type LucideIcon,
} from "lucide-react";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import {
  useCandidateProfileById,
  useCandidateSkills,
  useCandidateProjects,
  useCandidateEducation,
  useCandidateExperiences,
  useCandidateCertifications,
  useCandidateAwards,
} from "./hooks/useCandidateProfile";

import { CandidateHeader } from "./components/common/CandidateHeader";
import { CandidateKpis } from "./components/common/CandidateKpis";
import { CandidateSidebar } from "./components/common/CandidateSidebar";

import { PersonalTab } from "./components/tabs/PersonalTab";
import { ProfessionalTab } from "./components/tabs/ProfessionalTab";
import { ExperienceTab } from "./components/tabs/ExperienceTab";
import { SkillsTab } from "./components/tabs/SkillsTab";
import { ProjectsTab } from "./components/tabs/ProjectsTab";
import { EducationTab } from "./components/tabs/EducationTab";
import { CertificationsTab } from "./components/tabs/CertificationsTab";
import { AwardsTab } from "./components/tabs/AwardsTab";

import type { ProfileSkillDetail } from "@/interface/profileSkill";
import type { ProjectDetail } from "@/interface/project";
import type { EducationDetail } from "@/interface/education";
import type { CertificationDetail } from "@/interface/certification";
import type { AwardDetail } from "@/interface/award";
import type { ExperienceDetail } from "@/interface/profile";

type TabType =
  | "Personal"
  | "Professional"
  | "Experience"
  | "Skills"
  | "Projects"
  | "Education"
  | "Certifications"
  | "Awards";

const TAB_ITEMS: { id: TabType; label: string; icon: LucideIcon }[] = [
  { id: "Personal", label: "Personal Info", icon: UserCircle },
  { id: "Professional", label: "Professional Info", icon: Briefcase },
  { id: "Experience", label: "Experience", icon: Building2 },
  { id: "Skills", label: "Skills", icon: Award },
  { id: "Projects", label: "Projects", icon: FolderGit2 },
  { id: "Education", label: "Education", icon: GraduationCap },
  { id: "Certifications", label: "Certifications", icon: FileText },
  { id: "Awards", label: "Awards", icon: Trophy },
];

export default function CandidateProfileView() {
  const { id } = useParams<{ id?: string }>();
  const { data: user } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<TabType>("Personal");

  const effectiveId = id || user?.id;

  const { data: profileRes, isLoading: isProfileLoading } =
    useCandidateProfileById(effectiveId, Boolean(effectiveId));

  const profile = profileRes?.data;
  const profileId = profile?.id;

  const { data: skillsData } = useCandidateSkills(profileId, { page: 1, page_size: 14 });
  const { data: projectsData } = useCandidateProjects(profileId, { page: 1, page_size: 10 });
  const { data: experiencesData } = useCandidateExperiences(profileId, { page: 1, page_size: 10 });
  const { data: educationData } = useCandidateEducation(profileId, { page: 1, page_size: 10 });
  const { data: certsData } = useCandidateCertifications(profileId, { page: 1, page_size: 12 });
  const { data: awardsData } = useCandidateAwards(profileId, { page: 1, page_size: 12 });

  const extractList = <T,>(queryResult: any): T[] => {
    if (!queryResult) return [];
    if (queryResult.pages) {
      return queryResult.pages.flatMap((page: any) =>
        Array.isArray(page?.data) ? page.data : page?.data?.results || [],
      );
    }
    if (Array.isArray(queryResult.data)) return queryResult.data;
    return queryResult.data?.results || [];
  };

  const skillsList: ProfileSkillDetail[] = extractList<ProfileSkillDetail>(skillsData);
  const projectsList: ProjectDetail[] = extractList<ProjectDetail>(projectsData);
  const experiencesList: ExperienceDetail[] = extractList<ExperienceDetail>(experiencesData);
  const educationList: EducationDetail[] = extractList<EducationDetail>(educationData);
  const certsList: CertificationDetail[] = extractList<CertificationDetail>(certsData);
  const awardsList: AwardDetail[] = extractList<AwardDetail>(awardsData);

  const getTotalItems = (queryResult: any, listFallbackLength: number): number => {
    if (!queryResult) return listFallbackLength;
    if (queryResult.pages && queryResult.pages.length > 0) {
      const firstPage = queryResult.pages[0];
      if (typeof firstPage?.data?.totalItems === "number") {
        return firstPage.data.totalItems;
      }
    }
    if (typeof queryResult.data?.totalItems === "number") {
      return queryResult.data.totalItems;
    }
    return listFallbackLength;
  };

  const totalSkills = getTotalItems(skillsData, skillsList.length);
  const totalProjects = getTotalItems(projectsData, projectsList.length);
  const totalEducation = getTotalItems(educationData, educationList.length);
  const totalCerts = getTotalItems(certsData, certsList.length);
  const totalAwards = getTotalItems(awardsData, awardsList.length);

  if (isProfileLoading) {
    return (
      <div className="flex justify-center items-center min-h-[600px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const fullName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
      profile.username
    : "Candidate Profile";

  const renderActiveTab = () => {
    switch (activeTab) {
      case "Personal":
        return <PersonalTab profile={profile} />;
      case "Professional":
        return <ProfessionalTab profile={profile} />;
      case "Experience":
        return <ExperienceTab profileId={profileId} experiences={experiencesList} />;
      case "Skills":
        return <SkillsTab profileId={profileId} skills={skillsList} />;
      case "Projects":
        return <ProjectsTab profileId={profileId} projects={projectsList} />;
      case "Education":
        return <EducationTab profileId={profileId} education={educationList} />;
      case "Certifications":
        return <CertificationsTab profileId={profileId} certs={certsList} />;
      case "Awards":
        return <AwardsTab profileId={profileId} awards={awardsList} />;
      default:
        return <PersonalTab profile={profile} />;
    }
  };

  return (
    <div className="p-6 max-[#1600px] mx-auto space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm font-medium text-slate-500">
        <Link to="/candidates" className="hover:text-primary transition-colors">
          Candidates
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-slate-900 dark:text-white">{fullName}</span>
      </div>

      {/* Candidate Hero Header */}
      <CandidateHeader
        profile={profile}
        skillsList={skillsList}
        totalSkills={totalSkills}
      />

      {/* KPI Stats */}
      <CandidateKpis
        profile={profile}
        projectsCount={totalProjects}
        skillsCount={totalSkills}
        certsCount={totalCerts}
        awardsCount={totalAwards}
        educationCount={totalEducation}
      />

      {/* Navigation Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-2">
        {TAB_ITEMS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-purple-600 text-purple-600 font-bold"
                : "border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main Content + Sidebar Grid */}
      <div className="flex flex-col xl:flex-row gap-6 items-start">
        <div className="flex-1 w-full min-w-0">{renderActiveTab()}</div>
        <CandidateSidebar
          profile={profile}
          skillsList={skillsList}
          projectsList={projectsList}
          educationList={educationList}
        />
      </div>
    </div>
  );
}
